/**
 * The client-side mirror of the server's NDA normalisation.
 *
 * The server decides whether a save publishes a version by hashing the *normalised* title and
 * body and comparing that to the current document. The panel has to be able to predict that
 * answer before the editor presses save — telling somebody afterwards that they have just sent
 * every user back to an acceptance screen is too late to be useful.
 *
 * Keep `normalizeNdaText` in step with NdaService.Hash. If they drift, the panel will promise
 * one thing and the server will do another.
 */

/** Line endings collapsed and the ends trimmed, exactly as the server does before hashing. */
export function normalizeNdaText(title: string, body: string): string {
  const normalisedTitle = (title ?? '').trim()
  const normalisedBody = (body ?? '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim()
  return `${normalisedTitle}\n${normalisedBody}`
}

/**
 * Whether two documents would hash the same, and so whether a save would be a no-op.
 *
 * Compares the normalised text rather than its digest: it is the same decision, it needs no
 * crypto, and it is synchronous, which is what lets the editor show the consequence as you type
 * rather than a beat behind.
 */
export function isSameNdaText(
  a: { title: string; body: string },
  b: { title: string; body: string },
): boolean {
  return normalizeNdaText(a.title, a.body) === normalizeNdaText(b.title, b.body)
}

/**
 * The content hash the server would store for this text, for showing the editor the exact
 * fingerprint people will be signing.
 *
 * Resolves to null where WebCrypto is unavailable (a non-secure context, e.g. plain http on a
 * LAN address). Cosmetic only — nothing about publishing depends on it.
 */
export async function computeNdaHash(title: string, body: string): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle
  if (!subtle) return null

  try {
    const bytes = new TextEncoder().encode(normalizeNdaText(title, body))
    const digest = await subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null
  }
}

/** First 12 characters of a hash — enough to eyeball, short enough to sit in a table cell. */
export function shortHash(hash?: string | null): string {
  if (!hash) return '—'
  return hash.slice(0, 12)
}
